import React, { useState } from 'react';
import { Laptop, Smartphone, Copy, Check, Clock, Briefcase, ShieldCheck } from 'lucide-react';

/**
 * Shown when a candidate opens their interview link on a phone or tablet.
 *
 * The job of this screen is not to say "no" — it is to make sure the candidate
 * still sits their interview. So it leads with the fact that nothing is lost,
 * gives them the link in a form they can move to a laptop, and states the time
 * in IST so they know how long they have to find one. A dead end here means a
 * candidate silently drops out of the process.
 */

const IST = 'Asia/Kolkata';

const formatIst = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return `${d.toLocaleString('en-IN', {
        timeZone: IST,
        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
    })} IST`;
};

const UnsupportedDevicePage = ({ session, deviceClass = 'mobile' }) => {
    const [copied, setCopied] = useState(false);
    const url = typeof window !== 'undefined' ? window.location.href : '';

    const template  = session?.template || {};
    const role      = template.position || template.title || null;
    const roundName = session?.roundName || null;
    const when      = formatIst(session?.scheduledAt);
    const noun      = deviceClass === 'tablet' ? 'tablet' : 'phone';

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
        } catch {
            // Clipboard is blocked in some mobile browsers without a secure
            // context. The link is on screen and selectable either way, so
            // this is a convenience, not the only route.
            return;
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-800 via-slate-900 to-black px-4 py-10 font-sans">
            <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900 to-slate-800 p-6 text-white shadow-2xl sm:p-8">

                <div className="mb-6 flex items-center gap-3">
                    <div className="flex items-center gap-1.5 rounded-xl border border-amber-400/30 bg-amber-400/10 p-2.5">
                        <Smartphone className="h-5 w-5 text-amber-400" aria-hidden />
                        <span className="text-amber-400" aria-hidden>&rarr;</span>
                        <Laptop className="h-5 w-5 text-emerald-400" aria-hidden />
                    </div>
                    <div>
                        <p className="text-[11px] font-medium uppercase tracking-wider text-amber-400">
                            Switch device
                        </p>
                        <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">
                            Please use a laptop or desktop
                        </h1>
                    </div>
                </div>

                <p className="mb-5 text-sm leading-relaxed text-slate-300">
                    This interview is proctored, and the proctoring needs a full desktop browser to
                    run correctly. On a {noun}, an ordinary interruption — an incoming call, a
                    notification, or the screen locking by itself — is enough to end your interview
                    early. Rather than risk that partway through, we stop you here.
                </p>

                <div className="mb-5 rounded-xl border border-emerald-400/25 bg-emerald-400/10 p-4">
                    <div className="flex gap-2.5">
                        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" aria-hidden />
                        <div>
                            <p className="text-sm font-semibold text-emerald-300">
                                Nothing has been lost
                            </p>
                            <p className="mt-1 text-sm leading-relaxed text-slate-300">
                                Your interview has not started and your link is still valid. Open the
                                same link on a laptop or desktop and begin from there.
                            </p>
                        </div>
                    </div>
                </div>

                {(role || roundName || when) && (
                    <dl className="mb-5 space-y-2.5 rounded-xl border border-white/10 bg-white/5 p-4">
                        {role && (
                            <div className="flex items-start gap-2.5">
                                <Briefcase className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                                <div>
                                    <dt className="text-[10px] uppercase tracking-wider text-slate-400">Role</dt>
                                    <dd className="text-sm font-medium text-white">
                                        {role}{roundName ? ` · ${roundName}` : ''}
                                    </dd>
                                </div>
                            </div>
                        )}
                        {when && (
                            <div className="flex items-start gap-2.5">
                                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                                <div>
                                    <dt className="text-[10px] uppercase tracking-wider text-slate-400">Scheduled for</dt>
                                    <dd className="text-sm font-medium text-white">{when}</dd>
                                </div>
                            </div>
                        )}
                    </dl>
                )}

                <p className="mb-2 text-[10px] uppercase tracking-wider text-slate-400">
                    Your interview link
                </p>
                <div className="mb-5 flex items-stretch gap-2">
                    <code className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap rounded-lg border border-white/10 bg-black/40 px-3 py-2.5 text-xs text-slate-300">
                        {url}
                    </code>
                    <button
                        type="button"
                        onClick={copy}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/15 px-3 text-xs font-semibold text-primary transition-colors hover:bg-primary hover:text-white"
                    >
                        {copied
                            ? <><Check className="h-3.5 w-3.5" aria-hidden />Copied</>
                            : <><Copy className="h-3.5 w-3.5" aria-hidden />Copy</>}
                    </button>
                </div>

                <p className="text-xs leading-relaxed text-slate-400">
                    Email the link to yourself, or sign in again on a laptop from the same invitation.
                    If you cannot get to a laptop before your scheduled time, reply to your interview
                    invitation and your hiring team can reschedule you.
                </p>
            </div>
        </div>
    );
};

export default UnsupportedDevicePage;
